const TOKEN_KEY = 'alight_token';
const USER_KEY = 'alight_user';
const REFRESH_KEY = 'alight_refresh';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getUser(): any {
  const raw = localStorage.getItem(USER_KEY);
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}
export function setSession(token: string, user: any, refreshToken?: string) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Dev'da vite proxy '/api' ni backend'ga uzatadi.
// Ishlab chiqarishda VITE_API_URL (masalan https://alight-api.onrender.com) ishlatiladi.
const API_URL = (import.meta as any).env?.VITE_API_URL || '';
const BASE = API_URL ? API_URL.replace(/\/$/, '') + '/api' : '/api';
/** To'liq API URL (masalan <img src> uchun). */
export const apiUrl = (p: string) => BASE + p;

/** Access muddati tugaganda refresh token bilan jimgina yangilash (bir vaqtda bitta so'rov). */
let refreshing: Promise<boolean> | null = null;
function tryRefresh(): Promise<boolean> {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = fetch(BASE + '/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const d = await r.json();
        setSession(d.accessToken, d.user, d.refreshToken);
        return true;
      })
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshing = null; }, 100); });
  }
  return refreshing;
}

async function request(method: string, path: string, body?: any, isForm = false, retried = false): Promise<any> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let payload: any = undefined;
  if (body && !isForm) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  } else if (isForm) {
    payload = body;
  }
  const res = await fetch(BASE + path, { method, headers, body: payload });
  // Login urinishidagi 401 — noto'g'ri parol; sessiya 401'i — avval jimgina yangilab ko'ramiz
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (!retried && (await tryRefresh())) {
      return request(method, path, body, isForm, true);
    }
    clearSession();
    if (!location.pathname.includes('/login')) location.href = '/login';
    throw new Error('Avtorizatsiya muddati tugadi');
  }
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    let msg = `Xato (${res.status})`;
    if (ct.includes('json')) {
      const j = await res.json();
      msg = Array.isArray(j.message) ? j.message.join(', ') : j.message || msg;
    }
    throw new Error(msg);
  }
  if (ct.includes('json')) return res.json();
  // ⚠ JSON kutgan joyga HTML kelsa — bu deyarli har doim bitta narsa: bundle
  //   backend manzilisiz yig'ilgan va so'rov saytning o'ziga ketib, index.html
  //   qaytgan. Blob qaytarsak, keyinroq «.map is not a function» bo'lib
  //   butun ilova yiqiladi va sabab ko'rinmay qoladi.
  if (ct.includes('html')) {
    throw new Error(
      `Server JSON o'rniga HTML qaytardi (${path}). Backend manzili noto'g'ri sozlangan — ` +
      'saytni yangilang; takrorlansa dasturchiga xabar bering.',
    );
  }
  return res.blob();
}

export const api = {
  get: (p: string) => request('GET', p),
  post: (p: string, b?: any) => request('POST', p, b),
  patch: (p: string, b?: any) => request('PATCH', p, b),
  del: (p: string) => request('DELETE', p),
  upload: (p: string, file: File, extra?: Record<string, string>) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = extra ? '?' + new URLSearchParams(extra).toString() : '';
    return request('POST', p + qs, fd, true);
  },
  download: async (p: string, filename: string) => {
    const blob = await request('GET', p);
    const url = URL.createObjectURL(blob as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },
};

// ── Valyuta kurslari ────────────────────────────────────────────────────────
// Kurs «1 USD = X birlik» ko'rinishida saqlanadi (1 USD = 6,75 ¥ = 12 000 so'm).
// Shuning uchun USD ga o'tkazish uchun BO'LAMIZ, ko'paytirmaymiz.
const RATES_KEY = 'alight_rates';
let RATES: Record<string, number> = (() => {
  try { return { USD: 1, ...JSON.parse(localStorage.getItem(RATES_KEY) || '{}') }; } catch { return { USD: 1 }; }
})();

export function setRates(list: any[]) {
  const r: Record<string, number> = { USD: 1 };
  for (const c of list || []) {
    const v = Number(c.rateToBase);
    if (c.code && v > 0) r[String(c.code).toUpperCase()] = c.isBase ? 1 : v;
  }
  RATES = r;
  try { localStorage.setItem(RATES_KEY, JSON.stringify(r)); } catch { /* xotira to'lgan bo'lishi mumkin */ }
}
export const getRates = () => RATES;

/** Istalgan valyutadagi summani USD ga o'giradi. Kurs noma'lum bo'lsa null. */
export function toUsd(amount: any, currency?: string | null): number | null {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? '').replace(/\s/g, '').replace(',', '.'));
  if (!isFinite(n)) return null;
  const cur = String(currency || 'USD').toUpperCase();
  if (cur === 'USD') return n;
  const rate = RATES[cur];
  if (!rate || rate <= 0) return null;
  return n / rate;
}

/** «≈ $58 032» ko'rinishidagi izoh. USD bo'lsa yoki kurs yo'q bo'lsa — bo'sh. */
export function usdHint(amount: any, currency?: string | null, dec = 0): string {
  if (String(currency || 'USD').toUpperCase() === 'USD') return '';
  const v = toUsd(amount, currency);
  if (v == null || !isFinite(v) || v === 0) return '';
  return '≈ $' + fmt(v, dec);
}

// ── Formatlash: minglik = probel, kasr = vergul (masalan 12 600 yoki 6,75) ──
export function fmt(n: any, dec = 0): string {
  if (n === null || n === undefined || n === '') return '—';
  const num = typeof n === 'number' ? n : parseFloat(String(n).replace(/\s/g, '').replace(',', '.'));
  if (isNaN(num)) return String(n);
  const fixed = dec > 0 ? num.toFixed(dec) : Math.round(num).toString();
  let [int, frac] = fixed.split('.');
  const neg = int.startsWith('-');
  if (neg) int = int.slice(1);
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // minglik ajratgich
  if (frac) frac = frac.replace(/0+$/, ''); // ortiqcha nollarni olib tashlash
  return (neg ? '-' : '') + (frac ? int + ',' + frac : int);
}
export function fmtDate(d: any): string {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('ru-RU');
}
export function fmtDateTime(d: any): string {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}
/** ISO sanani lokal <input type="date"> qiymatiga (yyyy-mm-dd) aylantiradi — UTC siljishisiz. */
export function toDateInput(d: any): string {
  if (!d) return '';
  const date = new Date(d);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

export const ROLE_NAMES: Record<string, string> = {
  admin: 'Administrator',
  zakaz_operator: 'Zakaz operatori',
  rastamojka: 'Rastamojka mutaxassisi',
  sotuv: 'Sotuv menejeri',
  kargo: 'Kargo (rastamojka ko\'rish)',
  rahbar: 'Rahbar',
  buxgalter: 'Buxgalter',
};

export const ORDER_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Qoralama', cls: 'b-draft' },
  confirmed: { label: 'Tasdiqlangan', cls: 'b-confirmed' },
  in_transit: { label: "Yo'lda", cls: 'b-transit' },
  partial: { label: 'Qisman qabul', cls: 'b-partial' },
  received: { label: "To'liq qabul", cls: 'b-received' },
  closed: { label: 'Yopilgan', cls: 'b-closed' },
  cancelled: { label: 'Bekor qilingan', cls: 'b-cancelled' },
};

/** Noma'lum status kelsa ham sahifa yiqilmaydi (badge sifatida statusning o'zi ko'rinadi). */
export function stBadge(map: Record<string, { label: string; cls: string }>, status: string) {
  return map[status] || { label: status || '—', cls: 'b-draft' };
}

export const CUSTOMS_STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Qoralama', cls: 'b-draft' },
  calculated: { label: 'Hisoblangan', cls: 'b-confirmed' },
  submitted: { label: 'Deklaratsiya topshirildi', cls: 'b-transit' },
  confirmed: { label: 'Tasdiqlangan', cls: 'b-received' },
  archive: { label: 'Arxiv', cls: 'b-closed' },
};
